import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'

import {
  acceptAllQualifyingScores,
  autoFlightRegistrations,
  setQualifyingClosed,
  setQualifyingScore,
  setQualifyingStatus,
  updateRegistration,
  updateTournamentInfo,
  watchQualifyingResults,
} from '../lib/data.js'

const METHODS = [
  { id: 'round',     label: 'Qualifying Round',  desc: 'Teams play a real round during a window.' },
  { id: 'handicap',  label: 'Handicap Only',     desc: 'Use submitted handicaps; no round.' },
  { id: 'blind',     label: 'Blind Draw',        desc: 'Random flight assignment.' },
  { id: 'committee', label: 'Committee Assigned', desc: 'Add teams to flights by hand.' },
  { id: 'prior',     label: 'Prior Tournament',  desc: 'Import last year\'s standings.' },
]

export default function QualifyingTab({ tournamentId, tournament }) {
  const info = tournament?.info || {}
  const method = info.qualifyingMethod || 'round'

  const setMethod = async (m) => {
    await updateTournamentInfo(tournamentId, { qualifyingMethod: m })
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="font-bold text-lg mb-2">Qualifying Method</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {METHODS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMethod(m.id)}
              className={`text-left rounded-xl px-3 py-2.5 transition ${method === m.id
                ? 'bg-green-deep text-white'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
              <div className="font-bold text-sm">{m.label}</div>
              <div className={`text-xs ${method === m.id ? 'opacity-80' : 'text-gray-500'}`}>{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {method === 'round'     && <QualRoundPanel tournamentId={tournamentId} tournament={tournament} />}
      {method === 'handicap'  && <HandicapPanel tournament={tournament} />}
      {method === 'blind'     && <BlindPanel />}
      {method === 'committee' && <CommitteePanel />}
      {method === 'prior'     && <PriorPanel />}

      <LiveQualifyingScores tournamentId={tournamentId} />

      <QualifyingStatusTable tournamentId={tournamentId} tournament={tournament} />
    </div>
  )
}

// ---------- Live Qualifying Scores ----------
//
// Subscribes to `tournaments/{id}/qualifying` and renders submissions in
// real time as teams finish their rounds via the qualifying scorecard.
// Sorted lowest-score-first so the leader is always on top.

function LiveQualifyingScores({ tournamentId }) {
  const [results, setResults] = useState({})
  useEffect(() => {
    if (!tournamentId) return
    const off = watchQualifyingResults(tournamentId, setResults)
    return () => off()
  }, [tournamentId])

  const rows = useMemo(() => {
    return Object.entries(results)
      .map(([id, r]) => ({ id, ...r }))
      .sort((a, b) => (Number(a.score) || 9999) - (Number(b.score) || 9999))
  }, [results])

  const fmtTime = (ts) => {
    if (!ts) return '—'
    const d = new Date(ts)
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg">Live Qualifying Scores</h3>
        <span className="text-xs text-gray-500">
          {rows.length === 0 ? 'No submissions yet' : `${rows.length} submission${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-600">
          Scores will appear here automatically as teams finish their qualifying round.
          Print the qualifying QR codes from the <b>QR Codes</b> tab and post at check-in.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 w-10">#</th>
                <th className="text-left py-2">Team</th>
                <th className="text-right py-2">Score</th>
                <th className="text-right py-2">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 font-bold text-gray-500">{i + 1}</td>
                  <td className="py-2 font-semibold">{r.teamName || r.id}</td>
                  <td className="py-2 text-right font-extrabold text-green-deep">{r.score ?? '—'}</td>
                  <td className="py-2 text-right text-xs text-gray-500">{fmtTime(r.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---------- Method panels ----------

function QualRoundPanel({ tournamentId, tournament }) {
  const info = tournament?.info || {}
  const navigate = useNavigate()
  const [opens, setOpens] = useState(info.qualifyingOpens || '')
  const [closes, setCloses] = useState(info.qualifyingCloses || '')
  const [savedAt, setSavedAt] = useState(0)
  const qualifyingClosed = !!info.qualifyingClosed

  // STEP 2 — Accept All Qualifying Scores. Walks the /qualifying node
  // and writes qualifyingScore + qualifyingStatus:'complete' onto each
  // matching registration; spawns a new registration when no match.
  const [acceptBusy, setAcceptBusy] = useState(false)
  const [acceptResult, setAcceptResult] = useState(null)
  const [acceptErr, setAcceptErr] = useState('')
  const onAcceptAll = async () => {
    setAcceptBusy(true); setAcceptErr(''); setAcceptResult(null)
    try {
      const r = await acceptAllQualifyingScores(tournamentId)
      setAcceptResult(r)
    } catch (e) { setAcceptErr(e?.message || String(e)) }
    finally { setAcceptBusy(false) }
  }

  // STEP 3 — Close Qualifying. Flips info.qualifyingClosed so the QR
  // scorecard locks ("Qualifying is closed.") and the admin gets the
  // closed badge.
  const [closeBusy, setCloseBusy] = useState(false)
  const onToggleClosed = async (next) => {
    setCloseBusy(true)
    try { await setQualifyingClosed(tournamentId, next) }
    finally { setCloseBusy(false) }
  }

  // STEP 4 — Build Tournament. Counts registrations with a numeric
  // qualifyingScore (assumed accepted via the button above) and offers
  // Manual / AI paths.
  const acceptedCount = useMemo(() => {
    return Object.values(tournament?.registrations || {})
      .filter((r) => Number.isFinite(Number(r?.qualifyingScore))).length
  }, [tournament?.registrations])
  const [buildBusy, setBuildBusy] = useState(false)
  const [buildErr, setBuildErr] = useState('')
  const onBuildManual = async (teamsPerFlight) => {
    setBuildBusy(true); setBuildErr('')
    try {
      const regs = tournament?.registrations || {}
      const numTeams = acceptedCount || Object.keys(regs).length
      // PBCC 4-Ball — the bracket structure requires a fixed flight count in
      // 10..14 (default 12). Everything else derives flights from teams ÷
      // teams-per-flight, clamped 1..20.
      const isPbcc = !!tournament?.info?.pbcc4Ball
      let numFlights
      if (isPbcc) {
        numFlights = 12
      } else {
        const tpf = Math.max(2, Math.min(20, Number(teamsPerFlight) || 8))
        numFlights = Math.max(1, Math.min(20, Math.ceil(numTeams / tpf)))
      }
      await autoFlightRegistrations(tournamentId, regs, {
        numFlights,
        sortBy: 'qualifyingScore',
      })
      // Jump to Flights & Teams tab. The URL fragment is used by some
      // surfaces; the cleanest deep-link is just /admin/t/{id}.
      navigate(`/admin/t/${tournamentId}`)
    } catch (e) { setBuildErr(e?.message || String(e)) }
    finally { setBuildBusy(false) }
  }
  const onBuildAI = () => {
    // The AI panel is mounted at /admin (dashboard) and per-tournament
    // (TournamentRoute). Inside a tournament, it's already on screen —
    // dispatching a window event lets it pre-fill + open without
    // adding a new prop drill chain.
    const command = `Build the PBCC 4-Ball tournament from the qualifying list with ${acceptedCount} teams`
    try {
      window.dispatchEvent(new CustomEvent('ai-prefill', { detail: { command, open: true } }))
    } catch {}
  }

  const save = async () => {
    await updateTournamentInfo(tournamentId, {
      qualifyingOpens: opens || null,
      qualifyingCloses: closes || null,
    })
    setSavedAt(Date.now())
  }

  const url = `${window.location.origin}/qualify?t=${tournamentId}`

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold">Qualifying Round Window</h3>
        {qualifyingClosed ? (
          <span className="text-xs uppercase tracking-widest font-extrabold rounded-full px-2 py-0.5"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#B91C1C' }}>
            🔒 Qualifying Closed
          </span>
        ) : null}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600">Opens</span>
          <input type="date" className="input" value={opens} onChange={(e) => setOpens(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600">Closes</span>
          <input type="date" className="input" value={closes} onChange={(e) => setCloses(e.target.value)} />
        </label>
      </div>
      <button className="btn-primary mt-3" onClick={save}>Save Window</button>
      {savedAt ? <span className="text-xs text-green-deep ml-3 font-bold">Saved.</span> : null}

      <div className="mt-5">
        <h4 className="text-sm font-bold uppercase tracking-widest text-gray-600 mb-2">Qualifying QR</h4>
        <p className="text-sm text-gray-700">Print and post at the pro shop. Any registered team can scan to play their qualifying round during the window.</p>
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <div className="bg-white p-2 rounded-xl border-2 border-dashed border-gray-300">
            <QRCodeCanvas value={url} size={150} includeMargin />
          </div>
          <div className="text-sm">
            <div className="font-mono text-xs break-all bg-gray-50 px-2 py-1 rounded">{url}</div>
            <button className="btn-secondary text-xs mt-2" onClick={() => navigator.clipboard?.writeText(url)}>Copy Link</button>
          </div>
        </div>
      </div>

      {/* STEP 2 + 3 — Accept scores into registrations; Close qualifying. */}
      <div className="mt-5 border-t border-gray-200 pt-4">
        <h4 className="text-sm font-bold uppercase tracking-widest text-gray-600 mb-2">Bridge to Registrations</h4>
        <p className="text-xs text-gray-600 mb-3 leading-snug">
          Move scores from <code>/qualifying</code> onto the matching <code>/registrations</code> so Auto-Flight and the AI tools can read them.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={onAcceptAll}
            disabled={acceptBusy}
            className="btn-primary text-sm">
            {acceptBusy ? 'Accepting…' : '✓ Accept All Qualifying Scores'}
          </button>
          <button
            type="button"
            onClick={() => onToggleClosed(!qualifyingClosed)}
            disabled={closeBusy}
            className={qualifyingClosed ? 'btn-secondary text-sm' : 'btn-secondary text-sm'}
            style={qualifyingClosed
              ? { background: '#FBBF24', color: 'var(--ge-primary)' }
              : { background: 'var(--ge-primary)', color: 'var(--ge-primary)' }
            }>
            {closeBusy ? '…' : qualifyingClosed ? '🔓 Re-open Qualifying' : '🔒 Close Qualifying'}
          </button>
        </div>
        {acceptResult ? (
          <p className="text-xs mt-2 font-bold" style={{ color: 'var(--ge-primary)' }}>
            ✓ Matched {acceptResult.matched} · Created {acceptResult.created} · Skipped {acceptResult.skipped} of {acceptResult.totalQualifying} qualifying entries.
          </p>
        ) : null}
        {acceptErr ? <p className="text-xs mt-2 font-bold" style={{ color: '#B91C1C' }}>{acceptErr}</p> : null}
      </div>

      {/* STEP 4 — Build Tournament from Qualifying List. */}
      {acceptedCount > 0 ? (
        <div className="mt-5 border-t border-gray-200 pt-4">
          <h4 className="text-sm font-bold uppercase tracking-widest text-gray-600 mb-2">
            Build Tournament ({acceptedCount} qualified team{acceptedCount === 1 ? '' : 's'})
          </h4>
          <p className="text-xs text-gray-600 mb-3 leading-snug">
            Auto-Flight by qualifying score (up to 20 flights) — or let the AI seed the PBCC profile.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onBuildManual(8)}
              disabled={buildBusy}
              style={{
                borderRadius: 10, padding: '12px 16px',
                fontWeight: 800, fontSize: 13, letterSpacing: '0.04em',
                textTransform: 'uppercase',
                background: 'var(--ge-primary)', color: 'var(--ge-primary)',
                cursor: buildBusy ? 'not-allowed' : 'pointer',
                opacity: buildBusy ? 0.6 : 1,
              }}>
              🛠 Build Manually
            </button>
            <button
              type="button"
              onClick={onBuildAI}
              disabled={buildBusy}
              style={{
                borderRadius: 10, padding: '12px 16px',
                fontWeight: 800, fontSize: 13, letterSpacing: '0.04em',
                textTransform: 'uppercase',
                background: 'var(--ge-primary)', color: 'var(--ge-primary)',
                cursor: buildBusy ? 'not-allowed' : 'pointer',
                opacity: buildBusy ? 0.6 : 1,
              }}>
              ✨ Build with AI
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2 leading-snug">
            Manual: Auto-Flight runs immediately with 8 teams per flight (PBCC default) and you land on Flights &amp; Teams.
            AI: opens the ✨ panel with a pre-filled command — confirm and the AI applies the PBCC 4-Ball profile.
          </p>
          {buildErr ? <p className="text-xs mt-2 font-bold" style={{ color: '#B91C1C' }}>{buildErr}</p> : null}
        </div>
      ) : null}
    </div>
  )
}

function HandicapPanel({ tournament }) {
  const list = collectAllTeams(tournament).filter((t) => t.handicap != null)
  return (
    <div className="card">
      <h3 className="font-bold mb-2">Handicap Order</h3>
      <p className="text-sm text-gray-700">Teams will be flighted in handicap order (best handicap first). Edit handicaps in Registration → table view.</p>
      <ol className="mt-3 text-sm space-y-1">
        {list.sort((a, b) => (a.handicap ?? 99) - (b.handicap ?? 99)).map((t, i) => (
          <li key={`${t._kind}-${t.id}`} className="flex justify-between border-b last:border-0 py-1">
            <span><b>#{i + 1}</b> · {t.name}</span>
            <span className="text-gray-500">HCP {t.handicap}</span>
          </li>
        ))}
        {list.length === 0 ? <li className="text-gray-500">No teams with handicaps yet.</li> : null}
      </ol>
    </div>
  )
}

function BlindPanel() {
  return (
    <div className="card">
      <h3 className="font-bold mb-2">Blind Draw</h3>
      <p className="text-sm text-gray-700">
        Skip qualifying entirely. Use <b>Flights & Teams → ⛳ Auto-Flight (Random)</b> when you're ready to assign.
      </p>
    </div>
  )
}

function CommitteePanel() {
  return (
    <div className="card">
      <h3 className="font-bold mb-2">Committee Assigned</h3>
      <p className="text-sm text-gray-700">
        Skip auto-flighting. Create flights and add teams manually in <b>Flights & Teams</b>.
      </p>
    </div>
  )
}

function PriorPanel() {
  return (
    <div className="card">
      <h3 className="font-bold mb-2">Prior Tournament</h3>
      <p className="text-sm text-gray-600 italic">
        Coming soon — import final standings from a previous tournament as the qualifying order.
        For now, copy the qualifying scores from last year's Results CSV into the Registration → CSV panel.
      </p>
    </div>
  )
}

// ---------- Status table ----------

function QualifyingStatusTable({ tournamentId, tournament }) {
  const list = useMemo(() => collectAllTeams(tournament), [tournament])
  const total = list.length
  const completed = list.filter((t) => t.qualifyingStatus === 'complete' || Number.isFinite(Number(t.qualifyingScore))).length
  const waived = list.filter((t) => t.qualifyingStatus === 'waived').length

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-lg">Qualifying Status</h3>
        <span className="text-sm text-gray-600">{completed} of {total} qualified · {waived} waived</span>
      </div>
      {list.length === 0 ? (
        <p className="text-gray-500 text-sm">No teams yet. Add via Registration tab.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Team</th>
                <th className="text-right py-2">HCP</th>
                <th className="text-right py-2">Score</th>
                <th className="text-left py-2">Date</th>
                <th className="text-right py-2">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <QualRow key={`${t._kind}-${t.id}`} tournamentId={tournamentId} team={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function QualRow({ tournamentId, team }) {
  const [editing, setEditing] = useState(false)
  const [score, setScore] = useState(team.qualifyingScore ?? '')

  const teamRef = team._kind === 'reg'
    ? { kind: 'reg', regId: team.id }
    : { kind: 'team', flightId: team._flightId, teamId: team.id }

  const save = async () => {
    if (score === '') {
      await setQualifyingScore(tournamentId, teamRef, null)
    } else {
      await setQualifyingScore(tournamentId, teamRef, Number(score))
    }
    setEditing(false)
  }

  const waive = async () => {
    await setQualifyingStatus(tournamentId, teamRef, 'waived')
  }

  const status = team.qualifyingStatus || (Number.isFinite(Number(team.qualifyingScore)) ? 'complete' : 'pending')

  return (
    <tr className="border-b last:border-0">
      <td className="py-2 font-semibold">{team.name}</td>
      <td className="py-2 text-right text-gray-600">{team.handicap ?? '—'}</td>
      <td className="py-2 text-right">
        {editing ? (
          <span className="inline-flex gap-1">
            <input type="number" className="w-16 text-sm border rounded px-1 py-0.5 text-right"
                   value={score} onChange={(e) => setScore(e.target.value)} />
            <button className="text-xs text-green-deep font-bold" onClick={save}>✓</button>
          </span>
        ) : (
          <button onClick={() => setEditing(true)} className="font-bold text-green-deep hover:underline">
            {team.qualifyingScore ?? '—'}
          </button>
        )}
      </td>
      <td className="py-2 text-xs text-gray-500">{team.qualifyingDate || '—'}</td>
      <td className="py-2 text-right">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
          status === 'complete' ? 'bg-green-100 text-green-800'
          : status === 'waived' ? 'bg-purple-100 text-purple-800'
          : 'bg-gray-100 text-gray-700'
        }`}>{status}</span>
      </td>
      <td className="py-2 text-right">
        <button className="text-xs text-purple-700 font-semibold" onClick={waive}>Waive</button>
      </td>
    </tr>
  )
}

// ---------- Helpers ----------

function collectAllTeams(tournament) {
  const out = []
  const regs = tournament?.registrations || {}
  for (const [id, r] of Object.entries(regs)) {
    if (r.flighted) continue // skip; the flighted version is the source of truth
    out.push({
      _kind: 'reg',
      id,
      name: r.teamName || (r.players || []).join(' / '),
      players: r.players || [],
      handicap: r.handicap ?? null,
      qualifyingScore: r.qualifyingScore ?? null,
      qualifyingDate: r.qualifyingDate || null,
      qualifyingStatus: r.qualifyingStatus || null,
    })
  }
  const flights = tournament?.flights || {}
  for (const [flightId, f] of Object.entries(flights)) {
    for (const [teamId, t] of Object.entries(f.teams || {})) {
      const info = t.info || {}
      out.push({
        _kind: 'team',
        _flightId: flightId,
        id: teamId,
        name: info.name || (info.players || []).join(' / '),
        players: info.players || [],
        handicap: info.handicap ?? null,
        qualifyingScore: info.qualifyingScore ?? null,
        qualifyingDate: info.qualifyingDate || null,
        qualifyingStatus: info.qualifyingStatus || null,
      })
    }
  }
  return out
}
